import React, { useState, useEffect } from 'react';
import DashBoardNavBar from './DashboardNavbar';
import ExperienceCard from './ExperienceCard';
import ExperienceModal from './ExperienceModal';
import ExperienceViewModal from './ExperienceViewModal';
import firebase from '../firebaseConfig';
import { Button } from 'react-bootstrap';
import '../styles/Experiences.css';
import { useAdminView } from '../contexts/AdminViewContext';

const Experiences = ({ user }) => {
  const [experiences, setExperiences] = useState([]);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userName, setUserName] = useState(false);
  const { isAdminView, viewedUser, exitAdminView } = useAdminView();

  useEffect(() => {
    if (user) {
      // Utiliser l'utilisateur visualisé si en mode admin, sinon l'utilisateur connecté
      const targetUser = viewedUser || user;
      const userRef = firebase.firestore().collection('users').doc(targetUser.uid);
      const unsubscribe = userRef.onSnapshot(snapshot => {
        if (snapshot.exists) {
          const userData = snapshot.data();
          setUserName(userData.username);
          // Experiences are stored in an array, sort them by id (timestamp) descending
          const sortedExperiences = (userData.experiences || []).sort((a, b) => b.id - a.id);
          setExperiences(sortedExperiences);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    }
  }, [user, viewedUser]);

  const handleOpenViewModal = (exp) => {
    setSelectedExperience(exp);
    setShowViewModal(true);
  };

  const handleOpenEditModal = (exp = null) => {
    setSelectedExperience(exp);
    setShowEditModal(true);
  };

  const handleCloseViewModal = () => setShowViewModal(false);
  const handleCloseEditModal = () => setShowEditModal(false);

  const handleSwitchToEdit = () => {
    setShowViewModal(false);
    // A brief delay to ensure a smooth transition between modals
    setTimeout(() => {
      setShowEditModal(true);
    }, 200);
  };

  const handleDeleteExperience = async (experienceId) => {
    if (window.confirm('Are you sure you want to delete this experience? This action cannot be undone.')) {
      const targetUser = viewedUser || user;
      const userRef = firebase.firestore().collection('users').doc(targetUser.uid);
      const userDoc = await userRef.get();
      const currentExperiences = userDoc.data().experiences || [];
      const updatedExperiences = currentExperiences.filter(exp => exp.id !== experienceId);
      await userRef.update({ experiences: updatedExperiences });
      handleCloseEditModal();
    }
  };

  const handleSaveExperience = async (experienceData) => {
    const targetUser = viewedUser || user;
    const userRef = firebase.firestore().collection('users').doc(targetUser.uid);

    if (selectedExperience) {
      // UPDATE logic: find the experience in the array and replace it
      const userDoc = await userRef.get();
      const currentExperiences = userDoc.data().experiences || [];
      const updatedExperiences = currentExperiences.map(exp => 
        exp.id === selectedExperience.id ? { ...selectedExperience, ...experienceData } : exp
      );
      await userRef.update({ experiences: updatedExperiences });

    } else {
      // CREATE logic: add a new experience to the array
      const newExperience = {
        ...experienceData,
        id: Date.now(), // Use a timestamp as a unique ID
      };
      await userRef.update({
        experiences: firebase.firestore.FieldValue.arrayUnion(newExperience)
      });
    }

    handleCloseEditModal();
  };

  return (
    <>
      <DashBoardNavBar userName={userName || user.displayName || user.email} boldItem={'experiences'} isAdminView={isAdminView} exitAdminView={exitAdminView} />
      <div className="container mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1>My Experiences</h1>
          {!isAdminView && <Button variant="primary" onClick={() => handleOpenEditModal()}>Add Experience</Button>}
        </div>
        <p className="text-center text-muted mb-4">A journal of your travels, events, and personal growth.</p>

        {loading ? (
          <p>Loading experiences...</p>
        ) : (
          <div className="row">
            {experiences.length > 0 ? (
              experiences.map((exp, index) => (
                <div key={exp.id} className="col-md-4 mb-4">
                  <ExperienceCard experience={exp} onClick={() => handleOpenViewModal(exp)} index={index} />
                </div>
              ))
            ) : (
              <p className="text-center">You haven't added any experiences yet. Click 'Add Experience' to start!</p>
            )}
          </div>
        )}

        <ExperienceViewModal 
          show={showViewModal}
          onHide={handleCloseViewModal}
          onSwitchToEdit={handleSwitchToEdit}
          experience={selectedExperience}
        />

        <ExperienceModal 
          show={showEditModal} 
          onHide={handleCloseEditModal} 
          onSave={handleSaveExperience} 
          onDelete={handleDeleteExperience}
          experience={selectedExperience} 
        />
      </div>
    </>
  );
};

export default Experiences;
